import React, { useCallback, useEffect, useMemo } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { useNodePerformanceContext } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Autocomplete, Stack, Typography } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import { useLearningContentsQuery } from '@/modules/corporate/graphql/queries/generated/contents';
import { RenderInput } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/Search/renderers';
import { AutocompleteRenderOptionState } from '@/modules/corporate/modules/database/fields/components/DatabaseSelect/components/OptionItem/types';
import { OrderEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import { useCourse } from '../../courseContext';
import CourseEntity from '../../entity/Course';
import RoleplayEmbedCard from './RoleplayEmbedCard';

interface RoleplayOption {
  id: string;
  name: string;
  conversationTimeLimit?: number | null;
}

const getOptionLabel = (option: RoleplayOption) => option.name;

const renderOption = (option: RoleplayOption, state: AutocompleteRenderOptionState, t: any) => (
  <Stack alignItems="center" className="max-w-full" gapX={8}>
    <Typography
      className="truncate"
      color={state.selected ? 'text-text-primary' : 'text-text-secondary'}
      variant="body-short-01">
      {option.name}
    </Typography>
    {option.conversationTimeLimit ? (
      <Typography className="flex-shrink-0" color="text-text-placeholder" variant="body-short-03">
        {t('Course:Builder.RoleplayDuration', { duration: option.conversationTimeLimit })}
      </Typography>
    ) : null}
  </Stack>
);

interface RoleplayCardContainerProps {
  updateAttribute: (arg0: Object) => void;
  roleplayId: string;
  editable: boolean;
  duration?: number;
  id: string;
}

const RoleplayCardContainer: React.FC<RoleplayCardContainerProps> = (props) => {
  const { t } = useTranslation(['Course', 'Roleplay']);
  const { updateAttribute, roleplayId, editable, duration, id } = props;
  const { setNodePerformance } = useNodePerformanceContext();
  const [inputValue, setValue, searchString] = useDebounceState('');

  const { course } = useCourse();

  const [{ data }] = useLearningContentsQuery({
    variables: {
      filters: {
        name: searchString,
        types: [ResourceTypeEnum.Roleplay],
        shareable: course.isSent ? true : undefined,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
      },
      sort: [{ field: 'relevance', order: OrderEnum.Desc }],
    },
    pause: !editable,
  });

  const options: RoleplayOption[] = useMemo(() => {
    if (data?.contents.edges?.length) {
      return data.contents.edges.map(({ node }) => {
        const { id, name } = node as { id: string; name: string };
        const conversationTimeLimit = (node as any).conversationTimeLimit ?? null;
        return { id, name, conversationTimeLimit };
      });
    }
    return [];
  }, [data?.contents.edges]);

  const onChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: RoleplayOption) => {
      updateAttribute({ roleplayId: value.id });
    },
    [updateAttribute],
  );

  const onInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: string) => {
      setValue(value);
    },
    [setValue],
  );

  const setRoleplayPerformance = useCallback(() => {
    id && setNodePerformance?.({ id, blockType: 'roleplay', progress: 100 });
  }, [id, setNodePerformance]);

  useEffect(() => {
    // set performance for empty block where roleplay is still to be attached
    if (!editable && !roleplayId && !duration) {
      setRoleplayPerformance();
    }
  }, [duration, editable, roleplayId, setRoleplayPerformance]);

  const _renderInput = useCallback(
    (params: any) =>
      RenderInput({
        params,
        onBlurPlaceholder: t('Course:Builder.AttachRoleplay'),
        onFocusPlaceholder: t('Course:Builder.SearchForRoleplay'),
      }),
    [t],
  );

  if (!CourseEntity.id(course)) {
    return null;
  }

  return (
    <div className={cn('relative my-32', { 'pointer-events-none': !CourseEntity.id(course) })}>
      {roleplayId ? (
        <RoleplayEmbedCard
          courseId={editable ? undefined : CourseEntity.id(course) || undefined}
          roleplayId={roleplayId}
        />
      ) : editable ? (
        <Autocomplete
          disableClearable
          disablePortal
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          optionLabelRenderer={(option: RoleplayOption, state: AutocompleteRenderOptionState) =>
            renderOption(option, state, t)
          }
          options={options}
          popupIcon={null}
          renderInput={_renderInput}
          size="large"
          onChange={onChange}
          onInputChange={onInputChange}
          onKeyDown={stopEventPropagation}
          onKeyPress={stopEventPropagation}
          onPaste={stopEventPropagation}
        />
      ) : (
        <div />
      )}
    </div>
  );
};

export default RoleplayCardContainer;
